import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";
import Navbar from "../../components/Navbar";

const faqs = [
  {
    question: "What is the benefit of ISO certification for organizations?",
    answer:
      "ISO certification helps organizations improve process efficiency, ensure regulatory compliance, enhance customer trust, and gain international recognition across markets."
  },
  {
    question: "Which HACCP level is suitable for my team?",
    answer:
      "HACCP Level 2 is ideal for food handlers, Level 3 for supervisors and quality staff, and Level 4 for managers, consultants, and auditors responsible for system design and oversight."
  },
  {
    question: "Are PECB and ISO Lead Auditor trainings internationally recognized?",
    answer:
      "Yes, PECB and ISO Lead Auditor trainings are globally recognized and aligned with international standards, making them valuable credentials worldwide."
  },
  {
    question: "Do you offer customized corporate training programs?",
    answer:
      "Absolutely. All corporate trainings are tailored to your industry, organizational maturity, workforce size, and strategic objectives."
  },
  {
    question: "Can you support audit preparation and documentation?",
    answer:
      "Yes, MBA Solutions provides end-to-end support including gap analysis, documentation development, internal audits, and certification readiness."
  }
];

const FAQs = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <>
    <Navbar />
    <section className="relative py-14 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gray-50">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] mb-3">
            <HelpCircle size={16} /> Frequently Asked Questions
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Got Questions? We’ve Got Answers
          </h2>
          <p className="text-gray-600 mb-10">
            Find quick answers to common questions about our certifications,
            trainings, audits, and consultancy services.
          </p>

          <div className="space-y-5">
            {faqs.map((faq, index) => {
              const isOpen = activeIndex === index;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-md border border-gray-100"
                >
                  <button
                    onClick={() => setActiveIndex(isOpen ? null : index)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                  >
                    <span className="font-semibold text-gray-900">
                      {faq.question}
                    </span>
                    <span className="flex-shrink-0 text-[var(--bg-primary)]">
                      {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="overflow-hidden px-5 pb-5"
                      >
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Right Visual */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <img
            src="https://images.unsplash.com/photo-1551836022-d5d88e9218df"
            alt="FAQs Illustration"
            className="rounded-3xl shadow-2xl"
          />
          <div className="absolute -bottom-6 -left-6 bg-[var(--bg-primary)] text-white rounded-2xl p-6 shadow-xl">
            <p className="text-lg font-bold">Need More Help?</p>
            <p className="text-sm opacity-90">Talk to our experts anytime</p>
          </div>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default FAQs;